/**
 * In-browser viewers: PDF (PDF.js), Word (docx-preview),
 * Excel (SheetJS + grid), PowerPoint (pptxviewjs). Images use a plain img.
 */

import * as React from 'react';
import { cn } from '@ai-content-hub/content-kit';
import { useElementUITranslations } from '../i18n/elementUI';
import { resolveMediaUrlsForViewer } from '@ai-content-hub/content-core';
import type { OfficeDocumentBlockContent } from '../block/types';
import { fetchDocumentBytes } from './viewers/fetchDocumentBytes';
import { isNotADocumentPayload, sniffDocumentPayload } from './viewers/documentPayload';
import { PdfJsViewer } from './viewers/PdfJsViewer';
import { DocxPreviewViewer } from './viewers/DocxPreviewViewer';
import { XlsxGridViewer } from './viewers/XlsxGridViewer';

const LEGACY_OFFICE_EXTENSIONS = new Set(['doc', 'ppt']);

const FETCH_AND_VIEW_EXTENSIONS = new Set(['pdf', 'docx', 'xlsx', 'xls', 'pptx']);

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp']);

function extensionFromFileName(fileName: string | undefined): string | undefined {
    if (!fileName || !fileName.includes('.')) {
        return undefined;
    }
    const ext = fileName.trim().split('.').pop()?.toLowerCase();
    return ext && ext.length > 0 ? ext : undefined;
}

function extensionFromUrl(url: string): string | undefined {
    try {
        const path = new URL(url, 'https://placeholder.local').pathname;
        const name = path.split('/').pop() ?? '';
        return extensionFromFileName(name);
    } catch {
        const q = url.split('?')[0];
        const seg = q.split('/').pop() ?? '';
        return extensionFromFileName(seg);
    }
}

export interface OfficeDocumentPlayerProps {
    data: Omit<OfficeDocumentBlockContent, 'type'>;
    className?: string;
}

export const OfficeDocumentPlayer: React.FC<OfficeDocumentPlayerProps> = ({ data, className }) => {
    const et = useElementUITranslations();
    const src = (data.src ?? '').trim();
    const [resolvedUri, setResolvedUri] = React.useState<string | null>(null);
    const [pdfPreviewUri, setPdfPreviewUri] = React.useState<string | null>(null);
    const [publicUrl, setPublicUrl] = React.useState<string | null>(null);
    const [resolving, setResolving] = React.useState(false);
    const [buffer, setBuffer] = React.useState<ArrayBuffer | null>(null);
    const [pdfPreviewBuffer, setPdfPreviewBuffer] = React.useState<ArrayBuffer | null>(null);
    const [loadingPdfPreview, setLoadingPdfPreview] = React.useState(false);
    const [loadingBytes, setLoadingBytes] = React.useState(false);
    /**
     * EAA-1501: two different failures used to be one string, and a third was invisible.
     * `'download'` is "the request failed"; `'server'` is "the request succeeded but the
     * body is not this document" — the case that used to reach pdf.js and come back out
     * as "Could not open this PDF file.", blaming a file the server never sent.
     */
    const [loadFailure, setLoadFailure] = React.useState<'download' | 'server' | null>(null);

    React.useEffect(() => {
        if (!src || typeof window === 'undefined') {
            setResolvedUri(null);
            setResolving(false);
            return;
        }

        let cancelled = false;
        setResolving(true);
        setResolvedUri(null);
        setPdfPreviewUri(null);
        setPdfPreviewBuffer(null);
        setPublicUrl(null);

        void (async () => {
            try {
                const result = await resolveMediaUrlsForViewer(src);
                 
                console.debug('[OfficeDocumentPlayer] resolved:', result);
                if (!result.resolved) {
                    // The URL is the bare storage path: signing failed. Whatever answers
                    // it may well not be the document, so say so here rather than let the
                    // format parser invent an explanation later.
                    console.warn(
                        '[OfficeDocumentPlayer] media URL could not be signed; fetching the '
                            + 'unsigned storage path, which may not return the document.',
                        { src },
                    );
                }
                if (!cancelled) {
                    setResolvedUri(result.url);
                    setPdfPreviewUri(result.pdfPreviewUrl ?? null);
                    setPublicUrl(result.publicUrl ?? null);
                }
            } catch (err) {
                // EAA-1501: this fallback was completely silent, so a thrown resolver left
                // no trace at all — the only visible symptom was a downstream parse error.
                console.error(
                    '[OfficeDocumentPlayer] resolveMediaUrlsForViewer threw; falling back to '
                        + 'the raw src.',
                    { src, error: err },
                );
                if (!cancelled) {
                    setResolvedUri(src);
                }
            } finally {
                if (!cancelled) {
                    setResolving(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [src]);

    const extension = React.useMemo(
        () =>
            extensionFromFileName(data.fileName) ??
            extensionFromUrl(src) ??
            (resolvedUri ? extensionFromUrl(resolvedUri) : undefined),
        [data.fileName, src, resolvedUri],
    );

    const needsFetchedBuffer = Boolean(extension && FETCH_AND_VIEW_EXTENSIONS.has(extension));

    React.useEffect(() => {
        if (!resolvedUri || !needsFetchedBuffer) {
            setBuffer(null);
            setLoadFailure(null);
            setLoadingBytes(false);
            return;
        }

        let cancelled = false;
        setLoadingBytes(true);
        setLoadFailure(null);
        setBuffer(null);

        void fetchDocumentBytes(resolvedUri)
            .then((b) => {
                if (cancelled) {
                    return;
                }
                // A 200 is not proof we got the document: an SPA/CDN host answers an
                // unknown path with its index page, and an API can answer with a JSON
                // error envelope. Both parse as "corrupt file" one layer down.
                const payloadKind = sniffDocumentPayload(b);
                if (isNotADocumentPayload(payloadKind)) {
                    console.error(
                        '[OfficeDocumentPlayer] the response body is not a document.',
                        { url: resolvedUri, extension, payloadKind, byteLength: b.byteLength },
                    );
                    setLoadFailure('server');
                    return;
                }
                setBuffer(b);
            })
            .catch((err: unknown) => {
                console.error('[OfficeDocumentPlayer] could not download the document.', {
                    url: resolvedUri,
                    error: err,
                });
                if (!cancelled) {
                    setLoadFailure('download');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingBytes(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [resolvedUri, needsFetchedBuffer, extension]);

    // Fetch PDF preview bytes for PPTX/PPT when available.
    React.useEffect(() => {
        if (!pdfPreviewUri) {
            setPdfPreviewBuffer(null);
            setLoadingPdfPreview(false);
            return;
        }

        let cancelled = false;
        setLoadingPdfPreview(true);
        void fetchDocumentBytes(pdfPreviewUri)
            .then((b) => {
                if (!cancelled) {
                    setPdfPreviewBuffer(b);
                }
            })
            .catch(() => {
                // PDF preview not ready yet or failed – fall back to original viewer.
                if (!cancelled) {
                    setPdfPreviewBuffer(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingPdfPreview(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [pdfPreviewUri]);

    if (!src) {
        return (
            <div
                className={cn(
                    'office-document-player office-document-player--empty text-muted-foreground italic p-6 rounded-lg border border-border bg-muted/20',
                    className,
                )}
            >
                No document URL configured
            </div>
        );
    }

    const displayName = (data.fileName ?? 'document').trim() || 'document';

    const loadFailureMessage =
        loadFailure === 'download'
            ? et.documentDownloadFailed
            : loadFailure === 'server'
              ? et.documentServerError
              : null;

    let viewportBody: React.ReactNode;

    if (resolving || !resolvedUri) {
        viewportBody = (
            <span className="office-document-player-loading text-sm text-muted-foreground">
                {resolving ? 'Loading document…' : ''}
            </span>
        );
    } else if (extension && LEGACY_OFFICE_EXTENSIONS.has(extension)) {
        viewportBody = (
            <div className="office-document-player-legacy flex flex-col items-center justify-center gap-4 p-8 text-center text-sm text-muted-foreground">
                <p className="office-document-player-legacy-message max-w-md">
                    In-browser preview supports .docx, .xlsx, .pptx, and .pdf. Older {extension === 'doc' ? '.doc' : '.ppt'} files
                    cannot be shown here. Download the file to open it locally.
                </p>
                <a
                    href={resolvedUri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="office-document-player-download inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground cursor-pointer transition-opacity duration-200 hover:opacity-90"
                    download={displayName}
                >
                    Download {displayName}
                </a>
            </div>
        );
    } else if (extension && IMAGE_EXTENSIONS.has(extension)) {
        viewportBody = (
            <div className="office-document-player-image-wrap flex min-h-0 flex-1 items-center justify-center overflow-auto p-4">
                <img
                    src={resolvedUri}
                    alt={(data.title ?? displayName).trim() || 'Document'}
                    className="office-document-player-image max-h-full max-w-full object-contain"
                />
            </div>
        );
    } else if (needsFetchedBuffer) {
        if (!buffer) {
            viewportBody = (
                <div className="office-document-player-fetch flex flex-col items-center justify-center gap-4 p-8 text-center text-sm text-muted-foreground">
                    <span>{loadFailureMessage ?? (loadingBytes ? 'Downloading for preview…' : '')}</span>
                    {loadFailure ? (
                        <a
                            href={resolvedUri}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="office-document-player-download inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground cursor-pointer transition-opacity duration-200 hover:opacity-90"
                            download={displayName}
                        >
                            Download {displayName}
                        </a>
                    ) : null}
                </div>
            );
        } else if (extension === 'pdf') {
            viewportBody = <PdfJsViewer data={buffer} className="min-h-0 min-w-0 flex-1" />;
        } else if (extension === 'docx') {
            viewportBody = <DocxPreviewViewer data={buffer} className="min-h-0 min-w-0 flex-1" />;
        } else if (extension === 'xlsx' || extension === 'xls') {
            viewportBody = <XlsxGridViewer data={buffer} className="min-h-0 min-w-0 flex-1 p-2" />;
        } else if (extension === 'pptx' || extension === 'ppt') {
            if (publicUrl) {
                // EAA-1681: Office Online's chromeless status bar (bottom-right
                // "..." flyout) exposes a Download option we cannot reach or
                // disable — it renders inside a cross-origin iframe
                // (view.officeapps.live.com), so no CSS/JS from this app can
                // select or restyle anything inside it. This overlay is a
                // cosmetic click-blocker over that corner only, NOT a security
                // control: the button's exact position can shift with iframe
                // size, locale, or Office Online updates, and a user can still
                // reach it via browser zoom/resize or right-click elsewhere in
                // the frame. Real protection lives on the backend (one-time
                // serve tokens) and in the watermarked slide viewer (Layer 3).
                viewportBody = (
                    // EAA-1681: the click-blocker wrap div added an extra nesting level
                    // around the iframe. `h-full` on the iframe needs a percentage height
                    // to resolve, but this div only had `flex-1` (no `flex`/`flex-col`), so
                    // it never became a flex container — the iframe's height% had no
                    // definite containing block and fell back to the browser's intrinsic
                    // iframe height (~150px). Making the wrap a flex-col container and
                    // giving the iframe `flex-1` (matching how it filled the viewport
                    // before this wrap div existed) makes it stretch to the full height again.
                    <div className="office-document-player-pptx-wrap relative flex min-h-0 min-w-0 flex-1 flex-col">
                        <iframe
                            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(publicUrl)}`}
                            className="min-h-0 min-w-0 flex-1 border-0"
                            width="100%"
                            height="100%"
                            title={displayName}
                            allowFullScreen
                        />
                        <div
                            aria-hidden="true"
                            title=""
                            className="office-document-player-pptx-download-blocker pointer-events-auto absolute bottom-0 right-0 h-8 w-16 cursor-default"
                            onClick={(e) => e.preventDefault()}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    </div>
                );
            } else if (resolving) {
                viewportBody = (
                    <div className="flex min-h-0 flex-1 items-center justify-center text-sm text-muted-foreground">
                        Loading presentation…
                    </div>
                );
            } else {
                viewportBody = (
                    <div className="flex min-h-0 flex-1 items-center justify-center text-sm text-muted-foreground">
                        Could not load presentation preview.
                    </div>
                );
            }
        } else {
            viewportBody = (
                <div className="office-document-player-unknown flex flex-col items-center justify-center gap-4 p-8 text-center text-sm text-muted-foreground">
                    <p className="office-document-player-unknown-message max-w-md">{et.previewFailed}</p>
                    <a
                        href={resolvedUri}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="office-document-player-download inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground cursor-pointer transition-opacity duration-200 hover:opacity-90"
                        download={displayName}
                    >
                        Download {displayName}
                    </a>
                </div>
            );
        }
    } else {
        viewportBody = (
            <div className="office-document-player-unknown flex flex-col items-center justify-center gap-4 p-8 text-center text-sm text-muted-foreground">
                <p className="office-document-player-unknown-message max-w-md">
                    Preview is not available for this file type
                    {extension ? ` (.${extension})` : ''}. Download to open it.
                </p>
                <a
                    href={resolvedUri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="office-document-player-download inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground cursor-pointer transition-opacity duration-200 hover:opacity-90"
                    download={displayName}
                >
                    Download {displayName}
                </a>
            </div>
        );
    }

    const isCenteredLoader =
        resolving ||
        !resolvedUri ||
        (needsFetchedBuffer && !buffer) ||
        (extension && LEGACY_OFFICE_EXTENSIONS.has(extension)) ||
        (!needsFetchedBuffer && !(extension && IMAGE_EXTENSIONS.has(extension)) && !loadingBytes);

    return (
        <div className={cn('office-document-player flex min-w-0 flex-col gap-2', className)}>
            {(data.title ?? '').trim() ? (
                <h4 className="office-document-player-title text-lg font-semibold">{data.title}</h4>
            ) : null}
            <div
                className={cn(
                    'office-document-player-viewport w-full rounded-lg border border-border bg-muted/10',
                    isCenteredLoader
                        ? 'flex min-h-[600px] items-center justify-center overflow-hidden'
                        : 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
                )}
                style={
                    resolving || !resolvedUri
                        ? undefined
                        : { height: 'min(95vh, 1200px)', minHeight: '600px' }
                }
            >
                {viewportBody}
            </div>
        </div>
    );
};
